import React, { useEffect, useState } from 'react';

interface LoadingBarProps {
  color?: string;
}

/**
 * The visual loading bar component
 */
const LoadingBar: React.FC<LoadingBarProps> = ({ color = "#00A300" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    // Start with a slow progress
    let timeoutId: NodeJS.Timeout | null = null;
    
    // When component mounts, make it visible after a tiny delay
    // to avoid showing during quick navigations
    const delayId = setTimeout(() => {
      setIsVisible(true);
      
      // Simulate loading progress
      let currentProgress = 0;
      const simulateProgress = () => {
        // Go slower as we get closer to 90%
        const nextStep = currentProgress < 30 ? 5 : 
                        currentProgress < 60 ? 3 : 
                        currentProgress < 80 ? 1 : 0.5;
                        
        if (currentProgress < 90) {
          currentProgress += nextStep;
          setProgress(currentProgress);
          timeoutId = setTimeout(simulateProgress, 200);
        }
      };
      
      simulateProgress();
    }, 100);
    
    // Return the cleanup function directly
    return () => {
      // Clean up timeouts
      if (delayId) clearTimeout(delayId);
      if (timeoutId) clearTimeout(timeoutId);
      
      // Full progress when unmounting (navigation complete)
      setProgress(100);
      
      // Keep it visible briefly then hide it
      const hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      
      // Clear the timeout when component unmounts
      clearTimeout(hideTimeout);
    };
  }, []);
  
  if (!isVisible) {
    return null;
  }
  
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-100 z-50">
      <div 
        className="h-full transition-all duration-300 ease-out"
        style={{ 
          width: `${progress}%`, 
          backgroundColor: color,
          boxShadow: `0 0 10px ${color}`
        }}
      />
    </div>
  );
};

export default LoadingBar;
